import { useCallback, useEffect, useState } from 'react';
import { formsApi } from '../api/formsApi';
import type { AuditEvent } from '../api/types';

const ACTION_LABELS: Record<string, string> = {
  FormCreated: 'Created',
  FormEdited: 'Edited',
  FormPublished: 'Published',
  DraftCreated: 'New draft',
  AccessModeChanged: 'Access mode',
  SubmissionsExported: 'Exported',
  SecretLinkCreated: 'Link minted',
  SecretLinkRevoked: 'Link revoked',
};

/** Shows the form's audit trail — who did what, when. */
export function AuditPanel({ formId }: { formId: string }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    formsApi
      .getAudit(formId)
      .then((rows) => {
        setEvents(rows);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load audit log'))
      .finally(() => setLoading(false));
  }, [formId]);

  useEffect(() => load(), [load]);

  return (
    <div className="secret-links">
      <div className="deliveries-header">
        <h3>Audit log</h3>
        <button type="button" onClick={load} disabled={loading}>Refresh</button>
      </div>
      <p className="muted">Key actions on this form — publishing, edits, access-mode changes, exports and secret-link changes.</p>

      {error && <p className="error">{error}</p>}
      {!error && events.length === 0 && !loading && <p className="muted">No activity recorded yet.</p>}

      {events.length > 0 && (
        <table className="audit-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Who</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.createdAt).toLocaleString()}</td>
                <td><span className="audit-action">{ACTION_LABELS[e.action] ?? e.action}</span></td>
                <td>{e.actorName ?? '—'}</td>
                <td>{e.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
